import { useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import DialogPageComponent from '@/components/DialogPageComponent'
import { useAccount } from '@/contexts/AccountContext'
import { SecretCodeLoginForm } from '@/components/SecretCodeLoginForm'

/**
 * Login Page Component
 * 
 * see:
 * https://developer.apple.com/help/account/capabilities/configure-sign-in-with-apple-for-the-web/
 * 
 */

export const Route = createFileRoute('/old-login')({
  component: LoginPage,
})

function LoginPage() {
  const navigate = useNavigate()
  const { dispatch } = useAccount()
  const [showSecretCodeForm, setShowSecretCodeForm] = useState(false)

  const handleLogin = () => {
    const authDomain = import.meta.env.VITE_MY_AUTH_DOMAIN || 'https://my.unwritten.media'
    window.location.href = `${authDomain}/login?app=product-sslides&to=/today`
  }

  const handleSecretCodeSuccess = (jwtToken: string) => {
    // Update authentication context with JWT token
    dispatch({
      type: 'LOGIN',
      payload: { jwtToken }
    })

    // Redirect to home page on success
    navigate({ to: '/' })
  }

  const handleBackToLogin = () => {
    setShowSecretCodeForm(false)
  }

  // Show secret code form if toggled
  if (showSecretCodeForm) {
    return (
      <DialogPageComponent>
        <SecretCodeLoginForm 
          onBack={handleBackToLogin}
          onSuccess={handleSecretCodeSuccess}
        />
      </DialogPageComponent>
    )
  }

  return (
    <DialogPageComponent>
      {/* Title */}
      <h1 className="text-2xl font-bold mb-2">Sermon Slides Login</h1>
      
      {/* Subtitle */}
      <p className="text-lg text-gray-700 dark:text-mist-300 mb-6">
        Login to manage your team's services.
      </p>
      
      {/* Apple Login Button */}
      <div className="mb-4">
        <button
          onClick={handleLogin}
          className="w-full bg-black hover:bg-gray-800 text-white font-semibold py-3 px-4 rounded-lg transition duration-300 flex items-center justify-center gap-2"
        >
          <svg 
            className="w-5 h-5" 
            viewBox="0 0 24 24" 
            fill="currentColor"
          >
            <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
          </svg>
          Sign in with Apple
        </button>
      </div>

      {/* Divider */}
      <div className="relative mb-4">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300 dark:border-mist-700"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-white dark:bg-mist-900 text-gray-500 dark:text-mist-500">or</span>
        </div>
      </div>

      {/* Secret Code Login Button */}
      <div className="mb-6">
        <button
          onClick={() => setShowSecretCodeForm(true)}
          className="w-full bg-white dark:bg-mist-900 hover:bg-gray-50 dark:hover:bg-mist-800 text-gray-700 dark:text-mist-300 font-semibold py-3 px-4 rounded-lg border-2 border-gray-300 dark:border-mist-700 transition duration-300 flex items-center justify-center gap-2"
        >
          <svg 
            className="w-5 h-5" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" 
            />
          </svg>
          Login with Secret Code
        </button>
      </div>
      
      {/* Bottom Information Text */}
      <p className="text-sm text-gray-600 dark:text-mist-400 mt-6">
        If you are looking to take notes, download our app instead, no login necessary!
      </p>
    </DialogPageComponent>
  )
}
